/**
 * MicroCoursePage
 *
 * Renders a single micro-course with:
 *  - Module list sidebar
 *  - Lesson content panel
 *  - Timed quiz with countdown, live trait delta preview, and immediate feedback
 *  - Trait boost animation on correct answers
 *  - Course completion celebration + navigation
 *
 * Route: /course/:courseId
 * Every quiz answer emits a TraitEvent via useTraitMutation.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import {
  BookOpen, ChevronRight, ChevronLeft, Clock, CheckCircle2,
  XCircle, Zap, Trophy, ArrowRight, Lock, BarChart3
} from "lucide-react";
import { getCourseById, MicroCourse, CourseModule, QuizQuestion } from "@/data/micro-courses";
import { useTraitMutation } from "@/hooks/useTraitMutation";
import traitList from "../../../traitList.json";

// ─────────────────────────────────────────────────────────────────────────────
// Trait name lookup
// ─────────────────────────────────────────────────────────────────────────────
const TRAIT_NAME: Record<string, string> = Object.fromEntries(
  (traitList as Array<{ id: string; full_name: string }>).map((t) => [t.id, t.full_name])
);

// ─────────────────────────────────────────────────────────────────────────────
// Timer hook
// ─────────────────────────────────────────────────────────────────────────────
function useCountdown(seconds: number, onExpire: () => void, active: boolean) {
  const [remaining, setRemaining] = useState(seconds);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setRemaining(seconds);
  }, [seconds]);

  useEffect(() => {
    if (!active) { if (ref.current) clearInterval(ref.current); return; }
    ref.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) { clearInterval(ref.current!); onExpire(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [active, onExpire]);

  return remaining;
}

// ─────────────────────────────────────────────────────────────────────────────
// Difficulty badge
// ─────────────────────────────────────────────────────────────────────────────
const DiffBadge = ({ d }: { d: 1 | 2 | 3 }) => {
  const map = { 1: ["Easy", "bg-emerald-500/20 text-emerald-300"], 2: ["Medium", "bg-amber-500/20 text-amber-300"], 3: ["Hard", "bg-rose-500/20 text-rose-300"] } as const;
  const [label, cls] = map[d];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Trait delta preview pill
// ─────────────────────────────────────────────────────────────────────────────
const TraitDeltaPill = ({ traitId, delta }: { traitId: string; delta: number }) => (
  <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
    <Zap className="w-3 h-3 text-amber-400" />
    <span className="text-xs text-white font-medium">{TRAIT_NAME[traitId] ?? traitId}</span>
    <span className="text-xs text-amber-300 font-bold">+{delta.toFixed(1)}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Lesson panel
// ─────────────────────────────────────────────────────────────────────────────
const LessonPanel = ({ module, onStartQuiz }: { module: CourseModule; onStartQuiz: () => void }) => (
  <div className="space-y-6 max-w-2xl mx-auto">
    <div className="space-y-2">
      <p className="text-indigo-300 text-sm font-semibold uppercase tracking-wider">Lesson</p>
      <h2 className="text-2xl font-bold text-white">{module.title}</h2>
      <p className="text-slate-300 text-lg italic">"{module.lesson.summary}"</p>
    </div>
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-3">
      <h3 className="text-white font-semibold">Key Points</h3>
      {module.lesson.keyPoints.map((point, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-indigo-500/30 border border-indigo-400/40 text-indigo-300 text-xs flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</div>
          <p className="text-slate-300 text-sm leading-relaxed">{point}</p>
        </div>
      ))}
    </div>
    <button
      onClick={onStartQuiz}
      className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg text-lg"
    >
      Start Quiz <ChevronRight className="w-5 h-5" />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Quiz question panel
// ─────────────────────────────────────────────────────────────────────────────
type QuizState = "answering" | "correct" | "incorrect" | "timeout";

const QuizPanel = ({
  question,
  qIndex,
  total,
  onAnswer,
}: {
  question: QuizQuestion;
  qIndex: number;
  total: number;
  onAnswer: (optionId: string, timeTaken: number, correct: boolean, rawDeltas: Record<string, number>) => void;
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [state, setState] = useState<QuizState>("answering");
  const startTime = useRef(Date.now());
  const timerActive = state === "answering";

  const handleTimeout = useCallback(() => {
    setState("timeout");
    setTimeout(() => onAnswer("", question.timeLimit, false, {}), 1500);
  }, [question.timeLimit, onAnswer]);

  const remaining = useCountdown(question.timeLimit, handleTimeout, timerActive);
  const timeRatio = remaining / question.timeLimit;
  const timerColor = timeRatio > 0.5 ? "bg-emerald-400" : timeRatio > 0.25 ? "bg-amber-400" : "bg-rose-400";

  const handleSelect = (optId: string) => {
    if (state !== "answering") return;
    const timeTaken = Math.round((Date.now() - startTime.current) / 1000);
    const opt = question.options.find((o) => o.id === optId)!;
    setSelected(optId);
    setState(opt.correct ? "correct" : "incorrect");
    setTimeout(() => onAnswer(optId, timeTaken, opt.correct, opt.rawTraitDeltas), 1800);
  };

  const correctOpt = question.options.find((o) => o.correct)!;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Progress + Timer */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-slate-400">
          <span>Question {qIndex + 1} of {total}</span>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span className={remaining <= 10 ? "text-rose-400 font-bold" : ""}>{remaining}s</span>
          </div>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full transition-all ${timerColor}`} style={{ width: `${(remaining / question.timeLimit) * 100}%` }} />
        </div>
      </div>

      {/* Question */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <DiffBadge d={question.difficulty} />
        </div>
        <h2 className="text-xl font-bold text-white leading-relaxed">{question.text}</h2>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((opt) => {
          const isSelected = selected === opt.id;
          const showResult = state !== "answering";
          const isCorrectOpt = opt.correct;

          let cls = "bg-white/5 border-white/10 hover:bg-white/10";
          if (showResult && isCorrectOpt) cls = "bg-emerald-500/20 border-emerald-400/50";
          else if (showResult && isSelected && !isCorrectOpt) cls = "bg-rose-500/20 border-rose-400/50";

          return (
            <button
              key={opt.id}
              onClick={() => handleSelect(opt.id)}
              disabled={state !== "answering"}
              className={`w-full text-left p-4 rounded-xl border transition-all ${cls}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold ${isSelected ? "border-white bg-white text-slate-900" : "border-white/30 text-white/50"}`}>
                  {opt.id}
                </div>
                <div className="flex-1">
                  <span className={`text-sm leading-relaxed ${isSelected ? "text-white font-medium" : "text-slate-300"}`}>{opt.text}</span>
                  {/* Show top trait deltas as preview on hover / selection */}
                  {isSelected && state === "answering" && Object.entries(opt.rawTraitDeltas).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {Object.entries(opt.rawTraitDeltas).slice(0, 3).map(([tid, delta]) =>
                        delta > 0 ? <TraitDeltaPill key={tid} traitId={tid} delta={delta} /> : null
                      )}
                    </div>
                  )}
                </div>
                {showResult && isCorrectOpt && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
                {showResult && isSelected && !isCorrectOpt && <XCircle className="w-5 h-5 text-rose-400 shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {(state === "correct" || state === "incorrect" || state === "timeout") && (
        <div className={`p-4 rounded-xl border text-sm ${state === "correct" ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-200" : "bg-rose-500/10 border-rose-400/30 text-rose-200"}`}>
          <p className="font-semibold mb-1">
            {state === "timeout" ? "⏱ Time's up!" : state === "correct" ? "✓ Correct!" : "✗ Not quite"}
          </p>
          <p>{question.explanation}</p>
          {state !== "correct" && (
            <p className="mt-2 text-white/70">Correct answer: <span className="font-bold text-white">{correctOpt.text}</span></p>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Module complete screen
// ─────────────────────────────────────────────────────────────────────────────
const ModuleComplete = ({
  module,
  score,
  total,
  isLastModule,
  onNext,
  traitBoosts,
}: {
  module: CourseModule;
  score: number;
  total: number;
  isLastModule: boolean;
  onNext: () => void;
  traitBoosts: string[];
}) => (
  <div className="space-y-6 max-w-2xl mx-auto text-center">
    <div className="w-16 h-16 bg-indigo-500/20 border border-indigo-400/30 rounded-2xl flex items-center justify-center mx-auto">
      {score >= total * 0.75 ? <Trophy className="w-8 h-8 text-amber-400" /> : <CheckCircle2 className="w-8 h-8 text-emerald-400" />}
    </div>
    <div className="space-y-2">
      <h2 className="text-2xl font-bold text-white">Module Complete!</h2>
      <p className="text-slate-400">{module.title}</p>
      <p className="text-4xl font-black text-white">{score}/{total} <span className="text-lg text-slate-400">correct</span></p>
    </div>
    {traitBoosts.length > 0 && (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3 flex items-center justify-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-amber-400" /> Traits Updated
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {traitBoosts.map((t) => (
            <span key={t} className="text-xs bg-indigo-500/20 border border-indigo-400/30 text-indigo-200 px-3 py-1 rounded-full font-medium">
              {TRAIT_NAME[t] ?? t}
            </span>
          ))}
        </div>
      </div>
    )}
    <button onClick={onNext} className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all">
      {isLastModule ? "Complete Course" : "Next Module"} <ArrowRight className="w-5 h-5" />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Course complete screen
// ─────────────────────────────────────────────────────────────────────────────
const CourseComplete = ({ course, totalScore, maxScore, navigate }: { course: MicroCourse; totalScore: number; maxScore: number; navigate: (p: string) => void }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-6 text-center space-y-8">
    <div className="w-20 h-20 bg-amber-500/20 border border-amber-400/30 rounded-2xl flex items-center justify-center">
      <Trophy className="w-10 h-10 text-amber-400" />
    </div>
    <div className="space-y-2">
      <p className="text-indigo-300 text-sm font-semibold uppercase tracking-widest">Course Complete</p>
      <h1 className="text-3xl font-bold text-white">{course.title}</h1>
      <p className="text-5xl font-black text-white mt-4">{Math.round((totalScore / maxScore) * 100)}<span className="text-xl text-slate-400">%</span></p>
      <p className="text-slate-400">{totalScore} out of {maxScore} correct</p>
    </div>
    <div className="flex gap-3 w-full max-w-sm">
      <button onClick={() => navigate("/career-learning-path")} className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all">
        My Courses
      </button>
      <button onClick={() => navigate("/dashboard")} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-1.5">
        Dashboard <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
type PagePhase = "lesson" | "quiz" | "module-complete" | "course-complete";

export default function MicroCoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { emitQuizResult, emitCourseCompletion } = useTraitMutation();

  const course = getCourseById(courseId ?? "");

  const [moduleIndex, setModuleIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<PagePhase>("lesson");
  const [moduleScore, setModuleScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [answeredTraits, setAnsweredTraits] = useState<string[]>([]);

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        Course not found.
      </div>
    );
  }

  const currentModule: CourseModule = course.modules[moduleIndex];
  const currentQuestion: QuizQuestion | undefined = currentModule.questions[questionIndex];
  const totalQuestions = course.modules.reduce((acc, m) => acc + m.questions.length, 0);
  const maxModuleScore = currentModule.questions.length;

  const handleStartQuiz = () => {
    setQuestionIndex(0);
    setModuleScore(0);
    setAnsweredTraits([]);
    setPhase("quiz");
  };

  const handleAnswer = useCallback(
    (optionId: string, timeTaken: number, correct: boolean, rawDeltas: Record<string, number>) => {
      // Emit quiz trait event
      emitQuizResult({
        questionId: currentQuestion?.id ?? "",
        courseId: course.id,
        courseName: course.title,
        correct,
        timeTakenSeconds: timeTaken,
        timeLimitSeconds: currentQuestion?.timeLimit ?? 30,
        rawTraitDeltas: rawDeltas,
      });

      if (correct) setTotalScore((s) => s + 1);
      const newModuleScore = moduleScore + (correct ? 1 : 0);
      setModuleScore(newModuleScore);

      // Collect traits touched this module
      const newTraits = [...new Set([...answeredTraits, ...Object.keys(rawDeltas)])];
      setAnsweredTraits(newTraits);

      const nextQIndex = questionIndex + 1;
      if (nextQIndex < currentModule.questions.length) {
        setQuestionIndex(nextQIndex);
      } else {
        setPhase("module-complete");
      }
    },
    [questionIndex, moduleScore, answeredTraits, course, currentModule, currentQuestion, emitQuizResult]
  );

  const handleNextModule = () => {
    const nextModuleIndex = moduleIndex + 1;
    if (nextModuleIndex >= course.modules.length) {
      // Course complete — emit completion bonus
      emitCourseCompletion(
        course.id,
        course.title,
        Math.round((totalScore / totalQuestions) * 100),
        course.difficulty,
        course.completionBonusDeltas
      );
      setPhase("course-complete");
    } else {
      setModuleIndex(nextModuleIndex);
      setQuestionIndex(0);
      setModuleScore(0);
      setAnsweredTraits([]);
      setPhase("lesson");
    }
  };

  if (phase === "course-complete") {
    return <CourseComplete course={course} totalScore={totalScore} maxScore={totalQuestions} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-900/80 backdrop-blur-md border-b border-white/10 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-indigo-300" />
            </div>
            <div>
              <p className="text-white font-bold text-sm truncate max-w-[180px] sm:max-w-xs">{course.title}</p>
              <p className="text-slate-400 text-xs">{currentModule.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Module {moduleIndex + 1}/{course.modules.length}</span>
            </div>
            {/* Overall progress bar */}
            <div className="hidden sm:flex items-center gap-2 w-24">
              <div className="flex-1 bg-white/10 rounded-full h-1">
                <div className="bg-indigo-400 h-1 rounded-full" style={{ width: `${((moduleIndex) / course.modules.length) * 100}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Module sidebar + content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Module tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {course.modules.map((mod, i) => {
            const done = i < moduleIndex;
            const active = i === moduleIndex;
            return (
              <button
                key={mod.id}
                disabled={i > moduleIndex}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  active ? "bg-indigo-600 text-white" :
                  done ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30" :
                  "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed"
                }`}
              >
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i > moduleIndex ? <Lock className="w-3.5 h-3.5" /> : null}
                {mod.title.split(":")[0]}
              </button>
            );
          })}
        </div>

        {/* Content area */}
        {phase === "lesson" && (
          <LessonPanel module={currentModule} onStartQuiz={handleStartQuiz} />
        )}
        {phase === "quiz" && currentQuestion && (
          <QuizPanel
            question={currentQuestion}
            qIndex={questionIndex}
            total={currentModule.questions.length}
            onAnswer={handleAnswer}
          />
        )}
        {phase === "module-complete" && (
          <ModuleComplete
            module={currentModule}
            score={moduleScore}
            total={maxModuleScore}
            isLastModule={moduleIndex === course.modules.length - 1}
            onNext={handleNextModule}
            traitBoosts={answeredTraits}
          />
        )}
      </div>
    </div>
  );
}
